import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, ChevronDown, ChevronUp } from 'lucide-react';

const LoginPage = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(form.email, form.password);
      toast.success('Login successful! / 登入成功！');
      navigate('/app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed / 登入失敗');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-fac-dark flex flex-col items-center justify-center px-4 pt-16 pb-8 safe-bottom">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center gap-3 mb-4">
            <img src="/logo192.png" alt="FAC Solutions" className="w-16 h-16 sm:w-20 sm:h-20 object-contain" />
          </Link>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white">
            FAC<span className="text-fac-green">VALVE</span>
          </h1>
          <p className="text-muted-foreground font-chinese mt-2">
            智慧灌溉閥門控制系統
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-fac-card border border-border rounded-2xl p-6 sm:p-8" data-testid="login-form-card">
          
          {/* Primary: Google Sign-In Button - BIG and prominent */}
          <div className="mb-6">
            <Button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="w-full h-14 sm:h-16 bg-white hover:bg-gray-50 text-gray-800 font-semibold flex items-center justify-center gap-3 rounded-xl shadow-lg active:scale-[0.98] transition-transform"
              data-testid="google-login-btn"
            >
              {googleLoading ? (
                <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
              ) : (
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="text-base sm:text-lg">
                {googleLoading ? 'Connecting...' : 'Continue with Google'}
              </span>
            </Button>
            <p className="text-center text-xs text-muted-foreground mt-2 font-chinese">
              使用 Google 帳號快速登入或註冊
            </p>
          </div>

          {/* Expandable Email Login Section */}
          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setShowEmailLogin(!showEmailLogin)}
              className="w-full flex items-center justify-center gap-2 py-3 text-muted-foreground hover:text-white transition-colors"
              data-testid="toggle-email-login"
            >
              <Mail className="w-4 h-4" />
              <span className="text-sm font-chinese">
                {showEmailLogin ? 'Hide email login' : 'Login with email'} / {showEmailLogin ? '隱藏' : '使用電子郵件登入'}
              </span>
              {showEmailLogin ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {/* Email Login Form - Collapsible */}
            <div className={`overflow-hidden transition-all duration-300 ${showEmailLogin ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="text-white font-chinese text-sm">
                    電子郵件 Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 bg-fac-dark border-border focus:border-fac-green text-white h-12 text-base rounded-lg"
                    placeholder="your@email.com"
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-white font-chinese text-sm">
                    密碼 Password
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="bg-fac-dark border-border focus:border-fac-green text-white h-12 pr-12 text-base rounded-lg"
                      placeholder="••••••••"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white p-2"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-fac-green hover:bg-fac-green/90 text-black font-bold h-12 rounded-lg active:scale-[0.98] transition-transform"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Loading...' : 'Login / 登入'}
                </Button>

                <div className="flex flex-col items-center gap-2 text-sm">
                  <Link to="/forgot-password" className="text-muted-foreground hover:text-fac-green transition-colors" data-testid="forgot-password-link">
                    Forgot Password? / 忘記密碼？
                  </Link>
                  <Link to="/register" className="text-fac-green hover:underline" data-testid="register-link">
                    Create Account / 建立帳號
                  </Link>
                </div>
              </form>

              {/* Demo credentials */}
              <div className="mt-4 p-3 bg-fac-dark/50 rounded-lg border border-border/50">
                <p className="text-xs text-muted-foreground font-chinese text-center">
                  Demo: <span className="text-white font-mono">demo@fac.com / demo123</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6 font-chinese">
          By continuing, you agree to our Terms of Service
          <br />
          繼續即表示您同意我們的服務條款
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
